import React, { useState, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import Results from '../components/Results';
import AdditionalInfoModal from '../components/AdditionalInfoModal';
import Search from '../components/Search';
import { SearchStack } from '../utils/SearchStack';
import useLocalStorage from '../utils/hooks/useLocalStorage';
import API from '../api';

import logoSrc from '../logo.svg';
import dellLogo from '../logos/dell.png';
import googleLogo from '../logos/google.png';
import marriottLogo from '../logos/marriott.png';
import royalbankLogo from '../logos/royalbank.png';
import walmartLogo from '../logos/walmart.png';

const ResultsContent = styled('div')`
  grid-column: results / bleed;
  grid-row: results / bleed;
`;

const ContentlyLogo = styled('img')`
  height: 30px;
  margin-bottom: 2em;
`;

const RemainingText = styled('span')`
  font-size: 0.625rem;
  text-transform: uppercase;
  color: #7a7a7a;
  margin-bottom: 4em;
`;

const SearchContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const LogoWrapper = styled('div')`
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-bottom: 25%;
  opacity: 0.8;
  pointer-events: none;
  img {
    width: 30px;
    filter: grayscale(100%);
  }
`;

const LogosInnerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

const LogosContainer = styled('div')`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 9em;
  background: #e8f7fa;
  text-align: center;
  @media screen and (max-width: 1000px) {
    display: none;
  }
`;

const PageContainer = styled('div')`
  display: grid;
  grid-template-columns: [first] 65px [results] auto [bleed] 12em [last];
  grid-template-rows: [first] 65px [results] auto [bleed] 65px [last];
  min-height: 100vh;
  overflow-y: hidden;
  @media screen and (max-width: 1000px) {
    grid-template-columns: [first] 30px [results] auto [bleed] 30px [last];
  }
`;

const searchStack = new SearchStack();

const ResultsPage = ({ history }) => {
  const [showModal, setShowModal] = useState(false);
  const [contributors, setContributors] = useState([]);
  const [{ skill, topics, email }] = useLocalStorage('formValues', {});
  const [totalSearches, setTotalSearches] = useLocalStorage('totalSearches', 5);
  const toggleShowFormModal = () => setShowModal(!showModal);

  const increaseSearches = () => {
    setTotalSearches(8);
  };

  const pluralize = (count, noun, suffix = 's') => `${count} ${noun}${count !== 1 ? suffix : ''}`;

  useEffect(() => {
    if (!email) {
      history.push('/');
    }
  }, [email, history]);

  const fetchContributors = useCallback(() => {
    const existingData = searchStack.find({ skill, topics });

    if (existingData && existingData.data.length) {
      setContributors(existingData.data);
    } else {
      API.get(`recommended_contributors?topic_names[]=${encodeURIComponent(topics)}&skill=${skill}`)
        .then(({ data }) => {
          searchStack.push({ skill, topics }, { data });
          setContributors(data);
        })
        .catch(() => {
          return history.push({
            pathname: '/',
            state: { error: true }
          });
        });
    }
  }, [history, skill, topics]);

  const numContributors = contributors.length;

  useEffect(() => {
    if (numContributors === 0) {
      fetchContributors();
    }
  }, [numContributors, fetchContributors]);

  const searchCount = searchStack.size;
  const searchesRemaining = totalSearches - searchCount;

  useEffect(() => {
    if (searchesRemaining <= 0) {
      setShowModal(true);
    }
  }, [searchesRemaining, totalSearches]);

  const searchDataTitle = skill
    ? `${skill.charAt(0).toUpperCase() + skill.slice(1)} - ${topics}`
    : '';

  return (
    <>
      <Helmet>
        <title>Contently Talent Search{searchDataTitle}</title>
      </Helmet>
      <PageContainer>
        <ResultsContent>
          <ContentlyLogo src={logoSrc} />
          <SearchContainer>
            <Search count={numContributors} searchesRemaining={searchesRemaining} />
            <RemainingText>{pluralize(searchesRemaining, 'search', 'es')} remaining</RemainingText>
          </SearchContainer>
          <AdditionalInfoModal
            onInfoFormSubmit={increaseSearches}
            isOpen={showModal}
            onClose={toggleShowFormModal}
          />
          <Results
            toggleModal={toggleShowFormModal}
            contributors={contributors}
            remainingSearches={searchesRemaining}
          />
        </ResultsContent>

        <LogosContainer>
          <LogosInnerWrapper>
            <LogoWrapper>
              <img src={dellLogo} alt="Dell" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={googleLogo} alt="Google" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={marriottLogo} alt="Marriott" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={royalbankLogo} alt="Royal Bank" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={walmartLogo} alt="Walmart" />
            </LogoWrapper>
          </LogosInnerWrapper>
        </LogosContainer>
      </PageContainer>
    </>
  );
};

ResultsPage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired
};

export default withRouter(ResultsPage);
